import type { ImmutableDictionary } from "shelving";
import { matchTemplate, renderTemplate } from "shelving";

/**
 * List of routes in `{ [path]: Component | redirect }` format.
 *
 * @param path String path starting with `/` slash, possibly containing `{named}` placeholders that get passed into the component.
 * @param Component React component (either function or class component) that gets created if this route matches.
 * @param redirect String path that, if the user goes to it, redirects the user to a new page.
 */
export type Routes = ImmutableDictionary<React.FunctionComponent<any> | React.ComponentClass<any> | string>; // eslint-disable-line @typescript-eslint/no-explicit-any

/** Return the first element in `routes` matching `path`. */
export function matchRoute(path: string, routes: Routes): React.ReactElement | null {
	for (const [route, MatchedPage] of Object.entries(routes)) {
		const props = matchTemplate(route, path);
		if (props) {
			if (typeof MatchedPage === "string") {
				console.debug("⋯ ROUTE", path, "→", route, "→", MatchedPage); // eslint-disable-line no-console
				return matchRoute(renderTemplate(MatchedPage, props), routes);
			}
			console.debug("✔ ROUTE", path, "→", route, "→", MatchedPage.name, props); // eslint-disable-line no-console
			return <MatchedPage path={path} {...props} />;
		}
	}
	console.error("✘ ROUTE", path); // eslint-disable-line no-console
	return null;
}

/**
 * Simple router that shows a component based on a supplied path.
 * - Not connected to the browser's forward/backward mechanism in any way.
 */
export function Router({ path, routes }: { path: string; routes: Routes }): React.ReactElement | null {
	return matchRoute(path, routes);
}
